import React, { useEffect, useRef, useState } from 'react';
import { BadgeCheckIcon } from 'lucide-react';
import { useInView } from 'framer-motion';
import { Button, Card, Section } from './Shared';
type Testimonial = {
  text: string;
  author: string;
  result: string;
};
const AnimatedCounter = ({
  from,
  to,
  duration = 2
}: {
  from: number;
  to: number;
  duration?: number;
}) => {
  const [count, setCount] = useState(from);
  const ref = useRef<HTMLSpanElement>(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-50px'
  });
  useEffect(() => {
    if (!isInView) return;
    let startTimestamp: number;
    const step = (timestamp: number) => {
      if (!startTimestamp) startTimestamp = timestamp;
      const progress = Math.min((timestamp - startTimestamp) / (duration * 1000), 1);
      setCount(Math.floor(progress * (to - from) + from));
      if (progress < 1) window.requestAnimationFrame(step);
    };
    window.requestAnimationFrame(step);
  }, [duration, from, isInView, to]);
  return <span ref={ref} data-id="element-80" data-mp="3j8se7xjcfdo9">{count}</span>;
};
const TestimonialCarousel = () => {
  const row1: Testimonial[] = [{
    text: 'Antes me preguntaban precio y yo no sabía qué decir. Con el Tomo 4 aprendí a responder sin dudar. La primera semana cerré 4 turnos que antes se me escapaban.',
    author: 'Valeria R. · Buenos Aires · Manicurista',
    result: '4 turnos cerrados'
  }, {
    text: 'El tomo de Reels me cambió la cabeza. Hoy sé exactamente qué publicar y por qué. Cada pieza de contenido tiene una intención clara.',
    author: 'Luciana C. · Córdoba · Lashista',
    result: '50 guiones listos'
  }, {
    text: 'La Guía de Promos Flash me salvó dos semanas con la agenda floja. Publiqué una promo el domingo a la noche y el lunes ya tenía 3 turnos confirmados.',
    author: 'Paula G. · Mendoza · Cosmetóloga',
    result: '3 turnos confirmados'
  }, {
    text: 'Las Plantillas de Canva me ordenaron el feed en una tarde. Antes mi Instagram parecía un collage. Ahora parece un negocio de verdad.',
    author: 'Karina M. · Santa Fe · Manicurista',
    result: 'Feed ordenado en 1 tarde'
  }];
  const row2: Testimonial[] = [{
    text: 'Subí mis precios el primer mes después de leer el Tomo 1. Pensé que iba a perder clientas. No perdí ninguna. Las que se fueron nunca valoraban mi trabajo.',
    author: 'Marcela S. · Rosario · Esteticista',
    result: 'Precios más altos'
  }, {
    text: 'Lo que más me sorprendió fue la Máquina de Re-Agendamiento. Nunca pensé que podía recuperar clientas que daba por perdidas. Recuperé 7 en un mes.',
    author: 'Antonella R. · Tucumán · Brow Artist',
    result: '7 clientas recuperadas'
  }, {
    text: 'Compré el Megakit y fue la mejor decisión. Tengo todo ordenado y cada semana aplico un plan claro. Se nota en la agenda.',
    author: 'Sofía B. · La Plata · Peluquera',
    result: 'Agenda más ordenada'
  }, {
    text: 'Soy buena en lo que hago pero nunca supe vender. El Megakit me dio las palabras exactas. Ahora vendo sin sentirme pesada ni insistente.',
    author: 'Natalia F. · Mar del Plata · Skincare',
    result: 'Ventas sin insistir'
  }];
  const renderCard = (testimonial: Testimonial, index: number) => <Card key={index} className="bg-white w-[300px] md:w-[400px] flex-shrink-0" data-id="element-81" data-mp="2686cmgbfchhg">
      <div className="flex items-center justify-between gap-4 mb-4" data-id="element-82" data-mp="2b2smgf1uy1zf">
        <span className="text-brand text-sm tracking-wide" data-id="element-83" data-mp="2fz0vrbtdj0yo">⭐⭐⭐⭐⭐</span>
        <span className="rounded-full border border-brand/50 bg-brand/10 px-3 py-1 font-montserrat text-[10px] font-bold uppercase tracking-wide text-dark" data-id="element-84" data-mp="2kydt4qhvfqfi">
          {testimonial.result}
        </span>
      </div>
      <p className="font-montserrat text-gray-700 italic mb-6 text-sm md:text-base" data-id="element-85" data-mp="2tyiqs8x9jagh">
        “{testimonial.text}”
      </p>
      <p className="font-montserrat text-xs md:text-sm font-bold text-dark" data-id="element-86" data-mp="uo0v85gg70rp">
        — {testimonial.author}
      </p>
    </Card>;
  return <section className="w-full overflow-hidden py-16 md:py-24 bg-base relative" aria-label="Resultados de profesionales que usan el Megakit" data-id="element-87" data-mp="4b46fr58o0ie">
      <div className="absolute left-0 top-0 bottom-0 w-16 md:w-32 bg-gradient-to-r from-base to-transparent z-10 pointer-events-none" data-id="element-88" data-mp="3vgvvsob6z945" />
      <div className="absolute right-0 top-0 bottom-0 w-16 md:w-32 bg-gradient-to-l from-base to-transparent z-10 pointer-events-none" data-id="element-89" data-mp="e77jvt5jjlkn" />

      <div className="group flex overflow-hidden mb-6" data-id="element-90" data-mp="3hphaubxq4xug">
        <div className="flex gap-6 px-3 animate-marquee-left group-hover:[animation-play-state:paused]" data-id="element-91" data-mp="3ho64c4cw9bzn">
          {[...row1, ...row1].map(renderCard)}
        </div>
      </div>

      <div className="group flex overflow-hidden" data-id="element-92" data-mp="3rhn03dwld15y">
        <div className="flex gap-6 px-3 animate-marquee-right group-hover:[animation-play-state:paused]" data-id="element-93" data-mp="3motwitcuzyvg">
          {[...row2, ...row2].map(renderCard)}
        </div>
      </div>
    </section>;
};
export const Hero = () => {
  return <>
      <div className="bg-dark text-base text-center py-3 px-4 font-montserrat text-sm font-medium tracking-wide" data-id="element-94" data-mp="2u06gbc6yvzor">
        💅 BÓVEDA DIGITAL™ — Herramientas reales para emprendedoras reales
      </div>

      <Section bg="base" className="pt-12 md:pt-20" data-id="element-95" data-mp="1eaos5fr523ly">
        <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-8" data-id="element-96" data-mp="zkznhaju7hgx">
          <div className="flex-1 text-center lg:text-left" data-id="element-97" data-mp="14jkgoq0o90vv">
            <div className="inline-flex items-center gap-2 rounded-full border border-brand/60 bg-brand/10 px-4 py-2 mb-6 font-montserrat text-xs font-bold uppercase tracking-wide text-dark" data-id="element-98" data-mp="14ju54962izzg">
              <BadgeCheckIcon className="h-4 w-4 text-brand" aria-hidden="true" data-id="element-99" data-mp="1x7q0najwilun" />
              Más de 1000 profesionales ya lo usan
            </div>

            <h1 className="font-playfair text-4xl md:text-6xl lg:text-7xl font-bold leading-[1.06] mb-6 text-dark" data-id="element-100" data-mp="1n9scso5yer2k">
              MÁS CLIENTAS. AGENDA LLENA.
              <span className="block mt-3 text-2xl md:text-4xl lg:text-5xl text-brand" data-id="element-101" data-mp="7s0c11ik6bdb">
                RESOLVÉ TU FALTA DE VENTAS ESTA MISMA SEMANA.
              </span>
              <span className="block mt-3 text-xl md:text-3xl lg:text-4xl text-dark" data-id="element-102" data-mp="2egivmh7i8si9">
                EL SISTEMA PRÁCTICO PARA RESULTADOS EN 7 DÍAS.
              </span>
            </h1>

            <p className="font-montserrat text-lg md:text-xl text-gray-700 mb-4 max-w-2xl mx-auto lg:mx-0" data-id="element-103" data-mp="24qz82y8ukah2">
              Tu talento merece un negocio organizado. Los guiones y herramientas que necesitás, listos para aplicar desde hoy.
            </p>

            <p className="font-montserrat text-base md:text-lg font-bold text-accent mb-8 max-w-2xl mx-auto lg:mx-0" data-id="element-104" data-mp="1vopz4ct88p5s">
              Scripts listos para copiar. Respuestas listas para pegar. Sistema listo para aplicar.
            </p>

            <div className="flex flex-wrap justify-center lg:justify-start gap-3 md:gap-6 mb-10 font-montserrat text-sm font-medium text-gray-800" data-id="element-105" data-mp="1gubmcu09abzk">
              <span className="flex items-center gap-1" data-id="element-106" data-mp="3g64n91q601hk">📄 100% PDF</span>
              <span className="hidden md:inline" data-id="element-107" data-mp="3b94iqv8yr6sk">·</span>
              <span className="flex items-center gap-1" data-id="element-108" data-mp="3jotu1bvbw4d3">⚡ Acceso en 5 min</span>
              <span className="hidden md:inline" data-id="element-109" data-mp="3jn0uegj4ukel">·</span>
              <span className="flex items-center gap-1" data-id="element-110" data-mp="3npdq0c4611xn">💅 Todo el rubro estética</span>
              <span className="hidden md:inline" data-id="element-111" data-mp="3sgvomtipuqfg">·</span>
              <span className="flex items-center gap-1" data-id="element-112" data-mp="3sjhw6t5lx5xo">🛡️ Garantía 7 días</span>
            </div>

            <Button href="#pricing" className="text-base w-full md:w-auto md:px-12 py-5 mb-4" data-id="element-113" data-mp="1coulo6wj4sm">
              SÍ, QUIERO RESULTADOS ESTA SEMANA
            </Button>

            <div className="flex flex-wrap justify-center lg:justify-start gap-2 md:gap-4 font-montserrat text-xs text-gray-500" data-id="element-114" data-mp="39qnpemi50k4j">
              <span data-id="element-115" data-mp="3eq0ms16mx9ld">🛡️ Garantía 7 días</span>
              <span data-id="element-116" data-mp="3ennvgxezlmvn">·</span>
              <span data-id="element-117" data-mp="p0ownk9nyhmi">🔒 Pago seguro</span>
              <span data-id="element-118" data-mp="ozn7kbvtbuls">·</span>
              <span data-id="element-119" data-mp="p2j7u6v4uvde">⚡ Acceso inmediato</span>
              <span data-id="element-120" data-mp="tz03rn8kqe11">·</span>
              <span data-id="element-121" data-mp="ty886cez8gar">💳 Mercado Pago</span>
            </div>
          </div>

          <div className="flex-1 w-full max-w-xl" data-id="element-122" data-mp="1f2slilqhtq8h">
            <img src="https://cdn.magicpatterns.com/uploads/ccHaBTMz5jA8HE8gUqT1d6/Mockup_mas_clientas_SIN_FONDO.png" alt="Megakit Más Clientas Mockup" className="w-full h-auto drop-shadow-[0_20px_50px_rgba(0,0,0,0.15)]" data-id="element-123" data-mp="1f1hjaqcp4qjh" />
          </div>
        </div>
      </Section>

      <Section bg="base" className="py-8 md:py-12 border-t border-gray-200" data-id="element-124" data-mp="1yrjjok4i3ot3">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center divide-y md:divide-y-0 md:divide-x divide-gray-200" data-id="element-125" data-mp="1ytntfip2zi1a">
          <div className="pt-4 md:pt-0" data-id="element-126" data-mp="27rzgxbunhzpi">
            <p className="font-playfair text-4xl md:text-5xl font-bold text-dark mb-2" data-id="element-127" data-mp="27qy34zm92z43">
              +<AnimatedCounter from={0} to={3001} duration={2.5} data-id="element-128" data-mp="22ug4mhhijx07" />
            </p>
            <p className="font-montserrat text-sm text-gray-600 uppercase tracking-wider" data-id="element-129" data-mp="ddf23ft0gye1">profesionales ya lo tienen</p>
          </div>
          <div className="pt-8 md:pt-0" data-id="element-130" data-mp="8gopj2gf0kxd">
            <p className="font-playfair text-4xl md:text-5xl font-bold text-dark mb-2" data-id="element-131" data-mp="2k2qtlzsnkb2p">
              <AnimatedCounter from={0} to={6} duration={1.5} data-id="element-132" data-mp="2p1k73s7xw22v" />
            </p>
            <p className="font-montserrat text-sm text-gray-600 uppercase tracking-wider" data-id="element-133" data-mp="2p3o8o3id8ozz">tomos completos</p>
          </div>
          <div className="pt-8 md:pt-0" data-id="element-134" data-mp="2fbqvir7srmym">
            <p className="font-playfair text-4xl md:text-5xl font-bold text-dark mb-2" data-id="element-135" data-mp="2faftavu02n9m">4.9/5</p>
            <p className="font-montserrat text-sm text-gray-600 uppercase tracking-wider" data-id="element-136" data-mp="21axxhwiae9qr">calificación ⭐</p>
          </div>
        </div>
      </Section>

      <TestimonialCarousel data-id="element-137" data-mp="1sbawhbtgz5y8" />
    </>;
};